import { useCallback, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowsRotate,
  faCircleExclamation,
  faDatabase,
  faHardDrive,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import {
  getProviderUsage,
  refreshProviderUsage,
  type ProviderUsageReportDto,
} from '../api/provider-storage-usage.api';

/**
 * PHYSICAL bucket usage, side by side with what the catalogue believes.
 *
 * ── THE DISTINCTION THIS COMPONENT EXISTS TO MAKE ──────────────────────────
 * "70 assets in the database" is NOT "70 files in the bucket", and the two
 * diverge for reasons an operator has to be able to see:
 *
 *   • a provider migration moves OWNERSHIP and deletes nothing, so after a flip
 *     the source provider's logical figure collapses while every physical object
 *     is still there;
 *   • soft-deleted assets keep their bytes — soft delete is metadata;
 *   • objects can exist in a bucket that the catalogue never knew about.
 *
 * So both figures are shown, both are labelled, and the physical one is stated
 * to be the source of truth for what is actually stored.
 *
 * ── COST DISCIPLINE ────────────────────────────────────────────────────────
 * Measuring physical usage is a FULL provider listing. This component therefore
 * calls the cached read on mount — which lists nothing if a reading already
 * exists — and only performs a real scan when the operator explicitly asks. The
 * age of the reading and whether it came from cache are always on screen, so a
 * stale number is never mistaken for a live one.
 */

function fmtBytes(value: string | null | undefined): string {
  if (value === null || value === undefined) return '—';
  // Decimal string, because the value can exceed Number.MAX_SAFE_INTEGER.
  let n: number;
  try {
    n = Number(BigInt(value));
  } catch {
    return '—';
  }
  if (!Number.isFinite(n)) return '—';
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB', 'TB', 'PB'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v >= 100 ? 0 : 1)} ${units[i]}`;
}

function fmtAge(seconds: number): string {
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`;
  return `${Math.round(seconds / 3600)}h ago`;
}

export interface ProviderStorageUsageCardProps {
  providerId: string;
  /** Gates the explicit re-measure only. Reading is a view-level operation. */
  canManage: boolean;
}

export function ProviderStorageUsageCard({ providerId, canManage }: ProviderStorageUsageCardProps) {
  const [report, setReport] = useState<ProviderUsageReportDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Cached read. Safe on render: the server lists the provider only if it has
  // never measured it.
  useEffect(() => {
    setLoading(true);
    void (async () => {
      try {
        const r = await getProviderUsage(providerId);
        if (mountedRef.current) {
          setReport(r);
          setError(null);
        }
      } catch (err) {
        if (mountedRef.current) {
          setError(err instanceof Error ? err.message : 'Could not read storage usage.');
        }
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    })();
  }, [providerId]);

  /** The ONLY path that performs a real listing. Explicit, and never automatic. */
  const doRefresh = useCallback(async () => {
    if (refreshing) return; // no duplicate scans
    setRefreshing(true);
    setError(null);
    try {
      const r = await refreshProviderUsage(providerId);
      if (mountedRef.current) setReport(r);
    } catch (err) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err.message : 'The re-measurement failed.');
      }
    } finally {
      if (mountedRef.current) setRefreshing(false);
    }
  }, [providerId, refreshing]);

  return (
    <div className="border-t border-border bg-card p-4" data-testid="provider-usage-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <FontAwesomeIcon icon={faHardDrive} className="h-4 w-4 text-muted-foreground" />
          Storage usage
        </h3>
        {canManage && (
          <button
            type="button"
            data-testid="provider-usage-refresh"
            disabled={refreshing || loading}
            onClick={() => void doRefresh()}
            className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-foreground disabled:cursor-not-allowed disabled:opacity-50"
          >
            <FontAwesomeIcon
              icon={refreshing ? faSpinner : faArrowsRotate}
              spin={refreshing}
              className="h-3 w-3"
            />
            {refreshing ? 'Measuring…' : 'Refresh usage'}
          </button>
        )}
      </div>

      {loading ? (
        <p className="mt-3 text-sm text-muted-foreground" data-testid="provider-usage-loading">
          <FontAwesomeIcon icon={faSpinner} spin className="mr-2 h-3 w-3" />
          Reading storage usage…
        </p>
      ) : error !== null && report === null ? (
        <p
          className="mt-3 flex items-start gap-2 text-sm text-destructive"
          data-testid="provider-usage-error"
        >
          <FontAwesomeIcon icon={faCircleExclamation} className="mt-0.5 h-4 w-4 shrink-0" />
          {error}
        </p>
      ) : report === null ? null : (
        <>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {/* ── PHYSICAL: what the provider actually holds ─────────────── */}
            <div className="rounded-lg border border-border p-3" data-testid="usage-physical">
              <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-foreground">
                <FontAwesomeIcon icon={faHardDrive} className="h-3 w-3" />
                Physical — in the bucket
              </p>
              {report.physical === null ? (
                <p className="mt-1 text-sm text-muted-foreground">
                  {report.error ?? 'Not measured.'}
                </p>
              ) : (
                <>
                  <p className="mt-1 text-xl font-semibold tabular-nums text-foreground">
                    {fmtBytes(report.physical.totalBytes)}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {report.physical.totalObjects.toLocaleString()} object
                    {report.physical.totalObjects === 1 ? '' : 's'}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground" data-testid="usage-freshness">
                    {report.cached ? 'Cached' : 'Measured now'} · {fmtAge(report.ageSeconds)}
                    {report.stale && (
                      <span className="ml-1 font-medium text-amber-700">· stale</span>
                    )}
                  </p>
                </>
              )}
            </div>

            {/* ── LOGICAL: what the catalogue believes it owns ───────────── */}
            <div className="rounded-lg border border-border p-3" data-testid="usage-logical">
              <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-foreground">
                <FontAwesomeIcon icon={faDatabase} className="h-3 w-3" />
                Logical — owned in the catalogue
              </p>
              <p className="mt-1 text-xl font-semibold tabular-nums text-foreground">
                {fmtBytes(report.logical.activeBytes)}
              </p>
              <p className="text-sm text-muted-foreground">
                {report.logical.activeAssets.toLocaleString()} active asset
                {report.logical.activeAssets === 1 ? '' : 's'}
              </p>
              {report.logical.softDeletedAssets > 0 && (
                <p className="mt-1 text-xs text-muted-foreground">
                  + {report.logical.softDeletedAssets.toLocaleString()} soft-deleted (
                  {fmtBytes(report.logical.softDeletedBytes)}) — still physically present
                </p>
              )}
            </div>
          </div>

          {report.error !== null && report.physical !== null && (
            <p className="mt-2 text-xs text-amber-700">
              Last measurement failed ({report.error}); the figures above are the previous reading.
            </p>
          )}

          {report.sample !== null && report.sample.byPrefix.length > 0 && (
            <details className="mt-3" data-testid="usage-breakdown">
              <summary className="cursor-pointer text-xs font-medium text-muted-foreground">
                Breakdown by key prefix
                {report.sample.truncated && ' (partial — sampled)'}
              </summary>
              <ul className="mt-2 space-y-1">
                {report.sample.byPrefix.slice(0, 8).map((row) => (
                  <li key={row.prefix} className="flex justify-between gap-3 text-xs">
                    <span className="truncate font-mono text-foreground">{row.prefix}/</span>
                    <span className="shrink-0 tabular-nums text-muted-foreground">
                      {row.objects.toLocaleString()} · {fmtBytes(row.bytes)}
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          )}

          <ul
            className="mt-3 list-disc space-y-1 pl-4 text-xs text-muted-foreground"
            data-testid="usage-caveats"
          >
            {report.caveats.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
